import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAnalytics } from '../analytics';
import useWichListCount from '../hooks/useWichListCount';
import useNotifications from '../hooks/useNotifications';
import { Bell, X, MessageSquare } from 'lucide-react';
import { FEEDBACK_FORM_URL } from './FeedbackBanner';
import formatImageSrc from '../lib/formatImageSrc';

const Navigation = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const desktopNotificationsRef = useRef(null);
  const mobileNotificationsRef = useRef(null);
  const mobileBellRef = useRef(null);
  const location = useLocation();
  const { trackEvent } = useAnalytics();
  const { count: wichListCount, loading: wichListLoading } = useWichListCount();
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    refresh: refreshNotifications,
    markAllRead,
    markOneRead
  } = useNotifications();

  const wichListHref = user ? `/profile/${user.id}?tab=want-to-try` : '/login';

  // Helper to check if a nav link is active
  const isActiveRoute = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    
    // Special handling for WichList vs Profile conflict
    // Both navigate to /profile, but we use query params to distinguish
    const searchParams = new URLSearchParams(location.search);
    const currentTab = searchParams.get('tab');
    
    if (path === '/wichlist') {
      // WichList is active only when on profile page with want-to-try tab
      return location.pathname.startsWith('/profile') && currentTab === 'want-to-try';
    }
    
    if (path === '/profile') {
      // Profile is active when on profile page WITHOUT want-to-try tab
      // (or no tab param, or any other tab like 'reviews' or 'lists')
      return location.pathname.startsWith('/profile') && currentTab !== 'want-to-try';
    }
    
    return location.pathname.startsWith(path);
  };

  // Helper to get nav link classes with active state
  const getNavLinkClasses = (path, additionalClasses = '') => {
    const isActive = isActiveRoute(path);
    const baseClasses = 'py-2 transition-colors focus:outline-none';
    const activeClasses = isActive 
      ? 'text-passion-red font-semibold border-b-2 border-passion-red' 
      : 'text-gray-700 hover:text-passion-red border-b-2 border-transparent';
    return `${baseClasses} ${activeClasses} ${additionalClasses}`.trim();
  };

  const renderWichListBadge = () => {
    if (!user) return null;
    // While the WichList count is still being resolved, avoid flashing "0"
    if (wichListLoading && wichListCount == null) {
      return null;
    }

    const safeCount = typeof wichListCount === 'number' ? Math.max(0, wichListCount) : 0;
    const displayValue = safeCount > 99 ? '99+' : String(safeCount);
    const badgeClasses = safeCount === 0
      ? 'text-gray-500 border-gray-200'
      : 'text-passion-red border-passion-red';

    return (
      <span className={`ml-2 text-xs px-2 py-0.5 rounded-full border ${badgeClasses}`}>
        {displayValue}
      </span>
    );
  };

  const handleWichListNavClick = (locationLabel) => {
    if (trackEvent) {
      trackEvent('wichlist_nav_clicked', { location: locationLabel });
    }
    setMobileMenuOpen(false);
  };

  const getActorDisplayName = (actor) => {
    if (!actor) return 'Someone';
    if (actor.name && actor.name.trim().length > 0) return actor.name.trim();
    if (actor.username) return actor.username;
    return 'Someone';
  };

  const truncateText = (text, limit = 120) => {
    if (!text) return '';
    return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
  };

  const buildNotificationCopy = (notification) => {
    const actorName = getActorDisplayName(notification.actor);
    const sandwichLabel = notification.sandwich?.name ? `"${notification.sandwich.name}"` : 'your sandwich';
    switch (notification.type) {
      case 'review_liked':
        return {
          title: `${actorName} liked your review of ${sandwichLabel}.`,
          snippet: truncateText(notification.review?.review_text)
        };
      case 'comment_on_review':
        return {
          title: `${actorName} commented on your review of ${sandwichLabel}.`,
          snippet: truncateText(notification.comment?.comment_text)
        };
      case 'reply_to_comment':
        return {
          title: `${actorName} replied to your comment on ${sandwichLabel}.`,
          snippet: truncateText(notification.comment?.comment_text)
        };
      case 'user_followed':
        return {
          title: `${actorName} started following you.`,
          snippet: ''
        };
      case 'wichlist_add':
        return {
          title: `${actorName} added your review of ${sandwichLabel} to their WichList.`,
          snippet: ''
        };
      case 'list_add': {
        const listName = notification.list?.name || notification.metadata?.list_name;
        const listCopy = listName ? ` "${listName}"` : '';
        return {
          title: `${actorName} added your review of ${sandwichLabel} to their list${listCopy}.`,
          snippet: ''
        };
      }
      case 'mention_in_comment':
        return {
          title: `${actorName} mentioned you in a comment on ${sandwichLabel}.`,
          snippet: truncateText(notification.comment?.comment_text)
        };
      default:
        return {
          title: `${actorName} sent you a notification.`,
          snippet: ''
        };
    }
  };

  const getNotificationHref = (notification) => {
    if (!notification?.target) return null;
    const { target } = notification;
    if (target.kind === 'profile' && target.user_id) {
      return `/profile/${target.user_id}`;
    }
    if (target.kind === 'comment' && target.review_id) {
      const sandwichId = target.sandwich_id || notification.sandwich?.id;
      if (!sandwichId) return null;
      const commentAnchor = target.comment_id
        ? `#comment-${target.review_id}:${target.comment_id}`
        : `#review-${target.review_id}`;
      return `/sandwich/${sandwichId}${commentAnchor}`;
    }
    if (target.kind === 'review' && (target.review_id || target.sandwich_id)) {
      const sandwichId = target.sandwich_id || notification.sandwich?.id;
      if (!sandwichId) return null;
      const reviewAnchor = target.review_id ? `#review-${target.review_id}` : '';
      return `/sandwich/${sandwichId}${reviewAnchor}`;
    }
    return null;
  };

  const handleNotificationClick = (notification, event, locationLabel = 'unknown') => {
    if (!notification) return;
    // Stop propagation to prevent backdrop from catching this click
    event?.stopPropagation();

    // Track notification engagement (low-cardinality params only).
    if (trackEvent) {
      trackEvent('notification_clicked', {
        notification_type: notification.type,
        target_kind: notification?.target?.kind || 'unknown',
        location: locationLabel
      });
    }
    
    // Mark as read (don't await - let navigation happen immediately)
    markOneRead(notification.id).catch(() => {});
    
    const targetHref = getNotificationHref(notification);
    
    // Check if we're navigating to the same page with a different hash
    // React Router won't scroll to the new hash if we're already on the same path
    if (targetHref) {
      const [targetPath, targetHash] = targetHref.split('#');
      const currentPath = location.pathname;
      
      if (targetPath === currentPath && targetHash) {
        // Same page, different hash - prevent default Link behavior and manually scroll
        event?.preventDefault();
        setNotificationsOpen(false);
        
        // Update the URL hash and scroll to element
        window.history.pushState(null, '', targetHref);
        
        // Small delay to ensure DOM is ready, then scroll to element
        setTimeout(() => {
          const element = document.getElementById(targetHash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a highlight effect
            element.classList.add('ring-2', 'ring-passion-red', 'ring-offset-2');
            setTimeout(() => {
              element.classList.remove('ring-2', 'ring-passion-red', 'ring-offset-2');
            }, 2000);
          }
        }, 100);
        return;
      }
    }
    
    // Close popup after a brief delay to allow Link navigation to proceed first
    // React would otherwise unmount the Link before navigation happens
    setTimeout(() => setNotificationsOpen(false), 50);
    
    // Let the Link handle navigation naturally via its `to` prop
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const handleNotificationsToggle = (locationLabel) => {
    if (!user) return;
    const nextState = !notificationsOpen;
    setNotificationsOpen(nextState);
    if (nextState) {
      refreshNotifications();
      if (trackEvent) {
        trackEvent('notifications_icon_clicked', { location: locationLabel });
      }
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    await markAllRead();
    // Close the popup after marking all as read for better UX flow
    setNotificationsOpen(false);
  };

  const renderNotificationBadge = () => {
    if (!user || unreadCount <= 0) return null;
    const displayValue = unreadCount > 99 ? '99+' : String(unreadCount);
    return (
      <span className="absolute -top-1 -right-1 rounded-full bg-passion-red px-1.5 py-0.5 text-[10px] font-semibold text-white">
        {displayValue}
      </span>
    );
  };

  useEffect(() => {
    if (!notificationsOpen) return;
    
    const handleClickOutside = (event) => {
      // Check if click is inside desktop notifications popup
      const isInsideDesktop = desktopNotificationsRef.current && 
        desktopNotificationsRef.current.contains(event.target);
      // Check if click is inside mobile notifications popup
      const isInsideMobile = mobileNotificationsRef.current && 
        mobileNotificationsRef.current.contains(event.target);
      // Check if click is on the mobile bell button (to prevent double-toggle)
      const isOnMobileBell = mobileBellRef.current && 
        mobileBellRef.current.contains(event.target);
      
      if (!isInsideDesktop && !isInsideMobile && !isOnMobileBell) {
        setNotificationsOpen(false);
      }
    };
    
    // Delay adding listener to prevent the opening click from immediately closing
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchend', handleClickOutside, { passive: true });
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, [notificationsOpen]);
  
  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="text-xl sm:text-2xl font-bold text-passion-red wordmark-font">
              Club:Sandwich
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-6 items-center">
            <Link to="/" className={getNavLinkClasses('/')}>
              Home
            </Link>
            <Link to="/search" className={getNavLinkClasses('/search')}>
              Search
            </Link>
            {user && (
              <>
                <Link to="/upload" className={getNavLinkClasses('/upload')}>
                  Upload
                </Link>
                <Link
                  to={wichListHref}
                  onClick={() => handleWichListNavClick('desktop')}
                  className={getNavLinkClasses('/wichlist', 'flex items-center')}
                >
                  WichList
                  {renderWichListBadge()}
                </Link>
                <Link to={`/profile/${user.id}`} className={getNavLinkClasses('/profile')}>
                  Profile
                </Link>
              </>
            )}
          </div>
          
          {/* Desktop Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="relative group">
                <button
                  className="flex items-center space-x-2 text-gray-700 hover:text-passion-red transition-colors py-2"
                >
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    {user.image_url ? (
                      <img
                        src={formatImageSrc(user.image_url)}
                        alt={user.username}
                        className="w-8 h-8 rounded-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span className="text-gray-500 font-semibold text-sm">
                        {user.username?.charAt(0).toUpperCase() || user.name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="text-sm">@{user.username}</span>
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 top-full pt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                  <div className="bg-white rounded-lg shadow-lg border border-gray-100 py-1">
                    <Link
                      to={`/profile/${user.id}`}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-passion-red transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Profile
                    </Link>
                    <Link
                      to="/settings"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-passion-red transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Settings
                    </Link>
                    <a
                      href={FEEDBACK_FORM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-passion-red transition-colors"
                    >
                      <MessageSquare size={16} />
                      Give Feedback
                    </a>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={logout}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-passion-red transition-colors text-left"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-passion-red text-white px-4 py-2 rounded-full hover:bg-red-600 transition-colors"
              >
                Login
              </Link>
            )}
          </div>

          {/* Desktop Notifications */}
          {user && (
            <div className="relative mr-2 hidden md:block" ref={desktopNotificationsRef}>
              <button
                type="button"
                className="relative rounded-full p-2 text-gray-700 hover:text-passion-red"
                onClick={() => handleNotificationsToggle('desktop')}
                aria-label="Notifications"
              >
                <Bell size={20} />
                {renderNotificationBadge()}
              </button>
              {notificationsOpen && (
                <div className="absolute right-0 mt-3 z-30 w-96 rounded-2xl border border-gray-100 bg-white text-gray-900 shadow-2xl">
                  <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                    <p className="text-sm font-semibold">Notifications</p>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        onClick={handleMarkAllNotificationsRead}
                        className="text-xs font-semibold text-passion-red hover:underline"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notificationsLoading ? (
                      <div className="space-y-2 px-4 py-4">
                        {[...Array(3)].map((_, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
                            <div className="flex-1">
                              <div className="mb-2 h-3 w-3/4 rounded bg-gray-200 animate-pulse" />
                              <div className="h-3 w-1/3 rounded bg-gray-200 animate-pulse" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-gray-500">
                        <p>No notifications yet.</p>
                        <Link
                          to="/upload"
                          onClick={() => setNotificationsOpen(false)}
                          className="mt-3 inline-flex items-center justify-center rounded-full bg-passion-red px-4 py-2 text-xs font-semibold text-white hover:bg-red-600"
                        >
                          Share a sandwich review
                        </Link>
                      </div>
                    ) : (
                      notifications.map((notification) => {
                        const { title, snippet } = buildNotificationCopy(notification);
                        const actorInitial = getActorDisplayName(notification.actor).charAt(0).toUpperCase();
                        const targetHref = getNotificationHref(notification);
                        return (
                          <Link
                            key={notification.id}
                            to={targetHref || '#'}
                            onClick={(e) => handleNotificationClick(notification, e, 'desktop')}
                            className={`flex w-full items-start gap-3 px-4 py-3 text-left text-sm transition hover:bg-gray-50 active:bg-gray-100 cursor-pointer ${
                              notification.read ? 'bg-white' : 'bg-passion-red/5'
                            }`}
                          >
                            <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full bg-gray-100 pointer-events-none">
                              {notification.actor?.image_url ? (
                                <img
                                  src={notification.actor.image_url}
                                  alt={getActorDisplayName(notification.actor)}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <span className="flex h-full w-full items-center justify-center text-gray-500 font-semibold">
                                  {actorInitial || 'C'}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 pointer-events-none">
                              <p className="text-gray-800">{title}</p>
                              {snippet && (
                                <p className="text-xs text-gray-600 mt-1 break-words">{snippet}</p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">{formatTimeAgo(notification.created_at)}</p>
                            </div>
                            {!notification.read && <span className="mt-1 h-2 w-2 rounded-full bg-passion-red pointer-events-none" />}
                          </Link>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Mobile Notifications & Menu */}
          <div className="md:hidden flex items-center space-x-2">
            {user && (
              <div className="relative" ref={mobileBellRef}>
                <button
                  type="button"
                  className="relative rounded-full p-2 text-gray-700 hover:text-passion-red"
                  onClick={() => handleNotificationsToggle('mobile')}
                  aria-label="Notifications"
                >
                  <Bell size={20} />
                  {renderNotificationBadge()}
                </button>
              </div>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-passion-red focus:outline-none focus:text-passion-red p-2"
              aria-label="Toggle mobile menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                className={`block px-3 py-3 hover:bg-gray-50 transition-colors text-lg ${
                  isActiveRoute('/') 
                    ? 'text-passion-red font-semibold bg-passion-red/5' 
                    : 'text-gray-700 hover:text-passion-red'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/search"
                className={`block px-3 py-3 hover:bg-gray-50 transition-colors text-lg ${
                  isActiveRoute('/search') 
                    ? 'text-passion-red font-semibold bg-passion-red/5' 
                    : 'text-gray-700 hover:text-passion-red'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Search
              </Link>
              {user && (
                <>
                  <Link
                    to="/upload"
                    className={`block px-3 py-3 hover:bg-gray-50 transition-colors text-lg ${
                      isActiveRoute('/upload') 
                        ? 'text-passion-red font-semibold bg-passion-red/5' 
                        : 'text-gray-700 hover:text-passion-red'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Upload
                  </Link>
                  <Link
                    to={wichListHref}
                    className={`block px-3 py-3 hover:bg-gray-50 transition-colors text-lg flex items-center justify-between ${
                      isActiveRoute('/wichlist') 
                        ? 'text-passion-red font-semibold bg-passion-red/5' 
                        : 'text-gray-700 hover:text-passion-red'
                    }`}
                    onClick={() => handleWichListNavClick('mobile')}
                  >
                    <span>WichList</span>
                    {renderWichListBadge()}
                  </Link>
                  <Link
                    to={`/profile/${user.id}`}
                    className={`block px-3 py-3 hover:bg-gray-50 transition-colors text-lg ${
                      isActiveRoute('/profile') 
                        ? 'text-passion-red font-semibold bg-passion-red/5' 
                        : 'text-gray-700 hover:text-passion-red'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Profile
                  </Link>
                </>
              )}
              
              {/* Mobile Auth Section */}
              <div className="border-t border-gray-200 pt-2">
                <a
                  href={FEEDBACK_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-3 text-gray-500 hover:text-passion-red hover:bg-gray-50 transition-colors text-base"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <MessageSquare size={16} />
                  Give Feedback
                </a>
                {user ? (
                  <>
                    <Link
                      to={`/profile/${user.id}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-3 py-3 text-gray-700 hover:text-passion-red hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        {user.image_url ? (
                          <img
                            src={formatImageSrc(user.image_url)}
                            alt={user.username}
                            className="w-8 h-8 rounded-full object-cover"
                            loading="lazy" decoding="async"
                          />
                        ) : (
                          <span className="text-gray-500 font-semibold text-sm">
                            {user.username?.charAt(0).toUpperCase() || user.name?.charAt(0).toUpperCase()}
                          </span>
                        )}
                    </div>
                      <span className="text-base">@{user.username}</span>
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full text-left px-3 py-3 text-gray-700 hover:text-passion-red hover:bg-gray-50 transition-colors text-lg"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="block mx-3 my-2 bg-passion-red text-white px-4 py-3 rounded-full hover:bg-red-600 transition-colors text-center text-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mobile Notifications Popup */}
        {user && notificationsOpen && (
          <>
            {/* Backdrop - visual only, click-outside handled by event listener */}
            <div
              className="md:hidden fixed inset-0 z-[9998] bg-black/10 pointer-events-none"
              aria-hidden="true"
            />
            <div 
              ref={mobileNotificationsRef}
              className="md:hidden fixed inset-x-0 top-16 z-[9999] mx-4 mt-2 rounded-2xl border border-gray-100 bg-white text-gray-900 shadow-2xl"
              style={{ touchAction: 'manipulation' }}
            >
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <p className="text-sm font-semibold">Notifications</p>
                <div className="flex items-center gap-3">
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={handleMarkAllNotificationsRead}
                      className="text-xs font-semibold text-passion-red hover:underline"
                    >
                      Mark all as read
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setNotificationsOpen(false)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    aria-label="Close notifications"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            <div className="max-h-80 overflow-y-auto">
              {notificationsLoading ? (
                <div className="space-y-2 px-4 py-4">
                  {[...Array(3)].map((_, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
                      <div className="flex-1">
                        <div className="mb-2 h-3 w-3/4 rounded bg-gray-200 animate-pulse" />
                        <div className="h-3 w-1/3 rounded bg-gray-200 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-gray-500">
                  <p>No notifications yet.</p>
                  <Link
                    to="/upload"
                    onClick={() => setNotificationsOpen(false)}
                    className="mt-3 inline-flex items-center justify-center rounded-full bg-passion-red px-4 py-2 text-xs font-semibold text-white hover:bg-red-600"
                  >
                    Share a sandwich review
                  </Link>
                </div>
              ) : (
                notifications.map((notification) => {
                  const { title, snippet } = buildNotificationCopy(notification);
                  const actorInitial = getActorDisplayName(notification.actor).charAt(0).toUpperCase();
                  const targetHref = getNotificationHref(notification);
                  return (
                    <Link
                      key={notification.id}
                      to={targetHref || '#'}
                      onClick={(e) => handleNotificationClick(notification, e, 'mobile')}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left text-sm transition hover:bg-gray-50 active:bg-gray-100 cursor-pointer ${
                        notification.read ? 'bg-white' : 'bg-passion-red/5'
                      }`}
                      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full bg-gray-100 pointer-events-none">
                        {notification.actor?.image_url ? (
                          <img
                            src={notification.actor.image_url}
                            alt={getActorDisplayName(notification.actor)}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-gray-500 font-semibold">
                            {actorInitial || 'C'}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 pointer-events-none">
                        <p className="text-gray-800">{title}</p>
                        {snippet && (
                          <p className="text-xs text-gray-600 mt-1 break-words">{snippet}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">{formatTimeAgo(notification.created_at)}</p>
                      </div>
                      {!notification.read && <span className="mt-1 h-2 w-2 rounded-full bg-passion-red pointer-events-none" />}
                    </Link>
                  );
                })
              )}
            </div>
          </div>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navigation;


